<template>
  <div class="product-carousel" :class="{ 'product-carousel_modal-type': isModalType }">
    <span v-if="isModalType" @click="emit('close')" class="product-carousel__close icon-cancel"></span>
    <!-- Custom navigation buttons -->
    <button
      v-if="isModalType && !isFirstSlide"
      type="button"
      aria-label="Previous image"
      class="product-carousel__nav-btn product-carousel__nav-btn_prev"
      @click="prevSlide"
    >
      <span aria-hidden="true">&#xe903;</span>
    </button>
    <button
      v-if="isModalType && !isLastSlide"
      type="button"
      aria-label="Next image"
      class="product-carousel__nav-btn product-carousel__nav-btn_next"
      @click="nextSlide"
    >
      <span aria-hidden="true">&#xe903;</span>
    </button>

    <Carousel
      :per-page="1"
      @pageChange="pageChanged"
      :navigationEnabled="false"
      :paginationEnabled="true"
      :navigateTo="activeSlide"
      navigationNextLabel=""
      navigationPrevLabel=""
      ref="carousel"
    >
      <Slide v-for="(image, index) in resources" :key="image.fileId || image.fileName || index">
        <img
          :src="baseUrlMedia + image.fileId + '/' + image.fileName + imgRatioMainImage"
          @error="handleImageError"
          :alt="getImageAlt(image)"
          class="product-carousel__image"
        />
      </Slide>
    </Carousel>
    <div class="product-carousel__bar">
      <div
        class="product-carousel__pagination"
        v-bar="{ horizontal: true }"
        ref="pagination"
        :class="scrollArrowsClass"
        v-resize="handleResize"
      >
        <div class="product-carousel__pagination-inner" ref="paginationInner" v-scroll-el="handleHorizontalScroll">
          <div class="product-carousel__pagination-list" ref="paginationList">
            <div
              class="product-carousel__pagination-item"
              v-for="(image, index) in resources"
              :key="image.fileId || image.fileName || index"
              @click="changePage(index)"
            >
              <img
                :src="baseUrlMedia + image.fileId + '/' + image.fileName + imgRatioPaginationImage"
                @error="handleImageError"
                :alt="getImageAlt(image)"
                class="product-carousel__pagination-image"
                :class="{
                  'product-carousel__pagination-image_active': activePaginationImage == index
                }"
              />
            </div>
          </div>
        </div>
        <div
          class="product-carousel__pagination-arrow product-carousel__pagination-arrow_left"
          :class="{ 'product-carousel__pagination-arrow_hidden': isLeftArrowHidden }"
        >
          <div class="product-carousel__pagination-arrow-inner" @click="scrollToPrevItem"></div>
        </div>
        <div
          class="product-carousel__pagination-arrow product-carousel__pagination-arrow_right"
          :class="{ 'product-carousel__pagination-arrow_hidden': isRightArrowHidden }"
        >
          <div class="product-carousel__pagination-arrow-inner" @click="scrollToNextItem"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';
  import { handleImageError } from '~/utils/imageFallback';

  // Define interfaces
  interface ImageResource {
    fileId?: string;
    fileName?: string;
    title?: string;
    external?: {
      title?: string;
    };
    internal?: {
      title?: string;
    };
  }

  interface Props {
    resources?: ImageResource[];
    isModalType?: boolean;
    activeSlide?: number;
    title?: string;
  }

  const emit = defineEmits<{
    close: [];
  }>();

  // Props
  const props = withDefaults(defineProps<Props>(), {
    resources: () => [],
    isModalType: false,
    activeSlide: 0,
    title: ''
  });

  // Replace Vue 2 filter with a method
  function getImageAlt(image: ImageResource): string {
    if (image?.external?.title) return image.external.title;
    if (image?.internal?.title) return image.internal.title;
    if (image?.title) return image.title;
    return 'image';
  }

  // Store
  const mainStore = useMainStore();
  const baseUrlMedia = computed(() => mainStore.baseUrlMedia);

  // State
  const activePaginationImage = ref(props.activeSlide);
  const imgRatioMainImage = '?width=1686&height=938';
  const imgRatioPaginationImage = '?width=339&height=192';
  const scrollArrowsClass = ref('');
  const paginationScrollWidth = ref(0);
  const paginationInnerScrollWidth = ref(0);
  const scrollLeftPosition = ref(0);

  // Refs for DOM
  const pagination = ref<HTMLElement | null>(null);
  const paginationInner = ref<HTMLElement | null>(null);
  const paginationList = ref<HTMLElement | null>(null);
  const carousel = ref<any>(null); // Use any for third-party carousel component

  // Computed
  const isScrollArrowsVisible = computed(() => paginationScrollWidth.value < paginationInnerScrollWidth.value);
  const isLeftArrowHidden = computed(() => scrollLeftPosition.value === 0);
  const isRightArrowHidden = computed(() => scrollLeftPosition.value + paginationScrollWidth.value === paginationInnerScrollWidth.value);

  // Derive current index directly from carousel instance when available
  const totalSlides = computed(() => props.resources?.length || 0);
  const currentIndex = computed(() => {
    if (carousel.value) {
      if (carousel.value.currentPage !== undefined) return carousel.value.currentPage;
      if (carousel.value.currentSlide !== undefined) return carousel.value.currentSlide;
    }
    return activePaginationImage.value || 0;
  });
  // Main carousel edge state (for custom prev/next visibility)
  const isFirstSlide = computed(() => currentIndex.value <= 0);
  const isLastSlide = computed(() => currentIndex.value >= totalSlides.value - 1);

  // Methods
  function goToSlide(event: KeyboardEvent) {
    if (event.key === 'ArrowRight') {
      if (!isLastSlide.value) nextSlide();
    } else if (event.key === 'ArrowLeft') {
      if (!isFirstSlide.value) prevSlide();
    }
  }

  function nextSlide() {
    if (!carousel.value || isLastSlide.value) return;
    if (typeof carousel.value.next === 'function') {
      carousel.value.next();
    } else if (typeof carousel.value.goToPage === 'function' && typeof carousel.value.getNextPage === 'function') {
      carousel.value.goToPage(carousel.value.getNextPage());
    }
  }

  function prevSlide() {
    if (!carousel.value || isFirstSlide.value) return;
    if (typeof carousel.value.prev === 'function') {
      carousel.value.prev();
    } else if (typeof carousel.value.goToPage === 'function' && typeof carousel.value.getPreviousPage === 'function') {
      carousel.value.goToPage(carousel.value.getPreviousPage());
    }
  }

  function changePage(index: number) {
    if (!carousel.value) return;
    if (typeof carousel.value.slideTo === 'function') {
      carousel.value.slideTo(index);
    } else if (typeof carousel.value.goToPage === 'function') {
      carousel.value.goToPage(index);
    }
  }

  function pageChanged() {
    if (!carousel.value) return;
    if (carousel.value.currentPage !== undefined) {
      activePaginationImage.value = carousel.value.currentPage;
    } else if (carousel.value.currentSlide !== undefined) {
      activePaginationImage.value = carousel.value.currentSlide;
    }
  }

  function scrollToPrevItem() {
    if (!paginationInner.value || paginationInner.value.scrollLeft === 0) return;
    const list = paginationInner.value.children[0];
    if (!list) return;
    const items = list.children;
    let target: HTMLElement | null = null;
    const currentLeft = paginationInner.value.scrollLeft;
    for (let i = 0; i < items.length; i++) {
      const el = items[i] as HTMLElement;
      if (currentLeft - el.offsetLeft > 0) {
        target = el; // farthest fully visible to the left
      } else {
        break;
      }
    }
    if (!target) {
      scrollLeftPosition.value = 0;
      return;
    }
    const position = currentLeft - target.offsetLeft;
    sideScroll(paginationInner.value, 'left', 1, position, 5);
  }

  function scrollToNextItem() {
    if (!paginationInner.value) return;
    const maxed = paginationInner.value.offsetWidth + paginationInner.value.scrollLeft === paginationInner.value.scrollWidth;
    if (maxed) return;
    const list = paginationInner.value.children[0];
    if (!list) return;
    const items = list.children;
    let target: HTMLElement | null = null;
    const currentLeft = paginationInner.value.scrollLeft;
    for (let i = 0; i < items.length; i++) {
      const el = items[i] as HTMLElement;
      if (el.offsetLeft - currentLeft > 0) {
        target = el;
        break;
      }
    }
    if (!target) return;
    const position = target.offsetLeft - currentLeft;
    sideScroll(paginationInner.value, 'right', 1, position, 5);
  }

  function checkActiveItemPosition(val: number) {
    if (!paginationList.value) return;
    const children = paginationList.value.children;
    const activeItem = children[val] as HTMLElement;
    if (!activeItem) return;

    let activeItemLeftPos = activeItem.offsetLeft;
    let activeItemRightPos = activeItemLeftPos + activeItem.offsetWidth;
    let paginationLeftPos = scrollLeftPosition.value;
    let paginationRightPos = paginationLeftPos + paginationScrollWidth.value;
    if (activeItemRightPos > paginationRightPos) {
      let position = activeItemRightPos - paginationRightPos;
      scrollToActiveItem(position, 'right');
    }
    if (activeItemLeftPos < paginationLeftPos) {
      let position = paginationLeftPos - activeItemLeftPos;
      scrollToActiveItem(position, 'left');
    }
  }

  function scrollToActiveItem(position: number, direction: string) {
    if (!paginationInner.value) return;
    sideScroll(paginationInner.value, direction, 1, position, 5);
  }

  function sideScroll(el: HTMLElement | null, direction: string, speed: number, distance: number, step: number) {
    if (!el) return;
    scrollArrowsClass.value = 'product-carousel__pagination_with-arrows product-carousel__pagination_scrolling';
    let scrollAmount = 0;
    let slideTimer = setInterval(() => {
      if (direction === 'left') {
        el.scrollLeft -= step;
      } else {
        el.scrollLeft += step;
      }
      scrollAmount += step;
      if (scrollAmount >= distance - step) step = 1;
      if (scrollAmount >= distance) {
        scrollArrowsClass.value = 'product-carousel__pagination_with-arrows';
        window.clearInterval(slideTimer);
      }
    }, speed);
  }

  function handleResize() {
    if (!pagination.value || !paginationInner.value) return;
    paginationScrollWidth.value = pagination.value.scrollWidth;
    paginationInnerScrollWidth.value = paginationInner.value.scrollWidth;
    if (isScrollArrowsVisible.value) {
      scrollArrowsClass.value = 'product-carousel__pagination_with-arrows';
    } else {
      scrollArrowsClass.value = '';
    }
  }

  function handleHorizontalScroll() {
    if (!paginationInner.value) return;
    scrollLeftPosition.value = paginationInner.value.scrollLeft;
    if (scrollLeftPosition.value <= 20) {
      scrollLeftPosition.value = 0;
    }
  }

  // Watchers
  watch(
    () => activePaginationImage.value,
    (val: number) => {
      checkActiveItemPosition(val);
    }
  );

  // Lifecycle
  onMounted(() => {
    nextTick(() => {
      activePaginationImage.value = props.activeSlide;
      if (pagination.value) paginationScrollWidth.value = pagination.value.scrollWidth;
      if (paginationInner.value) paginationInnerScrollWidth.value = paginationInner.value.scrollWidth;
      if (isScrollArrowsVisible.value) scrollArrowsClass.value = 'product-carousel__pagination_with-arrows';
    });
    document.addEventListener('keyup', goToSlide);
  });

  onUnmounted(() => {
    document.removeEventListener('keyup', goToSlide);
  });

  // Watchers
  watch(
    () => activePaginationImage.value,
    (val: number) => {
      checkActiveItemPosition(val);
    }
  );

  // Lifecycle
  onMounted(() => {
    nextTick(() => {
      activePaginationImage.value = props.activeSlide;
      if (pagination.value) paginationScrollWidth.value = pagination.value.scrollWidth;
      if (paginationInner.value) paginationInnerScrollWidth.value = paginationInner.value.scrollWidth;
      if (isScrollArrowsVisible.value) scrollArrowsClass.value = 'product-carousel__pagination_with-arrows';
    });
    document.addEventListener('keyup', goToSlide);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keyup', goToSlide);
  });
</script>
